/**
 * Footer renders universeSize from meta — never a hardcoded count.
 * When `stale`, strikes through the market-date AS OF and marks
 * `[data-stale="true"]` (plan §6.7 · FR-A8). `metaPhase` keeps loading/error
 * distinct from no-snapshot (`asOf: null` when ready).
 */
import styles from './app-footer.module.css';

import type { JSX } from 'react';

export type MetaPhase = 'loading' | 'ready' | 'error';

export interface AppFooterProps {
  readonly universeSize: number | null;
  readonly asOf: string | null;
  readonly stale?: boolean;
  /** Default `ready` preserves prior call sites. */
  readonly metaPhase?: MetaPhase;
}

function universeLabel(universeSize: number | null, metaPhase: MetaPhase): string {
  if (universeSize !== null) return `${universeSize.toLocaleString('en-US')} names`;
  if (metaPhase === 'error') return 'unavailable';
  return 'loading';
}

function asOfLabel(asOf: string | null, metaPhase: MetaPhase): string {
  if (metaPhase === 'loading') return 'loading';
  if (metaPhase === 'error') return 'unavailable';
  return asOf ?? 'synthetic / unpublished';
}

export function AppFooter({
  universeSize,
  asOf,
  stale = false,
  metaPhase = 'ready',
}: AppFooterProps): JSX.Element {
  return (
    <footer className={styles['footer']} {...(stale ? { 'data-stale': 'true' as const } : {})}>
      <span>Universe · {universeLabel(universeSize, metaPhase)}</span>
      <span className={stale ? styles['as-of-stale'] : undefined}>
        As of · {asOfLabel(asOf, metaPhase)}
      </span>
    </footer>
  );
}
